import React, { useCallback, useEffect, useState } from 'react';
import { AppBar, Grid, Paper, Toolbar, Typography } from '@mui/material';
import queryString from 'query-string';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  AdminTag,
  AdminTagService,
  AdminUserSearchResult,
  Company,
  CompanyEmployeeService,
  CompanyService,
  Parent,
  ParentLocalGroup,
  ParentLocalGroupsService,
  ParentService, User,
  UserAdminTagService,
  UserState,
} from 'us-web-services';
import PageStyles from '../../styles/PageStyles';

import {
  BackgroundCheck,
  IdentityCheck,
  ScammerOverride,
  SitterProfileStatus,
  UserAdminNotes,
  UserAdminTags,
  UserCard,
  UserDevices,
  UserNotifications,
  UserVouchers,
} from '.';
import LookupTabs from './LookupTabs';
import Partners from './Partners';
import PaymentInfo from './PaymentInfo';
import UserSocialConnections from './UserSocialConnections';
import UserSearch from './UserSearch';
import UserSearchResults from './UserSearchResults';
import RecentlyAccessedUsers from './RecentlyAccessedUsers';
import { useAuthentication, useUser } from '../../store';
import useLoadUserProfile from '../../hooks/useLoadUserProfile';
import ParentLocalGroups from './ParentLocalGroups';
import UserPhotoHistory from './UserPhotoHistory';
import UserVideoModeration from './UserVideoModeration';
import UserGroupEdit from './UserGroupEdit';
import SharedAccounts from './SharedAccounts';

function UserLookup({ location, history }: RouteComponentProps) {
  const { classes } = PageStyles();
  const authenticationState = useAuthentication()[0];
  const searchParams = queryString.parse(location.search);
  const [userCache, userActions] = useUser();
  const { loadUserById } = useLoadUserProfile();

  const [adminTags, setAdminTags] = useState<AdminTag[]>([]);
  const [localGroups, setLocalGroups] = useState<ParentLocalGroup[]>([]);
  const [userAdminTags, setUserAdminTags] = useState<AdminTag[]>([]);
  const [userCompany, setUserCompany] = useState<Company | null>(null);
  const [userParent, setUserParent] = useState<Parent | null>(null);
  const [userState, setUserState] = useState<UserState>({
    user: {},
    profile: {},
  });
  const urlSearchTerm = searchParams.search as string || '';
  const [searchTerm, setSearchTerm] = useState(urlSearchTerm);
  const [showSearchResults, setShowSearchResults] = useState(!!urlSearchTerm);

  useEffect(() => {
    setSearchTerm(urlSearchTerm);
    setShowSearchResults(!!urlSearchTerm);
    if (urlSearchTerm && !searchParams.id) {
      userActions.setUser({});
      userActions.setProfile({});
      setUserState({ user: {}, profile: {} });
    }
  }, [urlSearchTerm, searchParams.id, userActions]);

  useEffect(() => {
    const urlUserId = searchParams.id ? Number(searchParams.id) : null;

    if (urlUserId && (!userCache.user.id || userCache.user.id !== urlUserId)) {
      loadUserById(urlUserId);
    }
  }, [searchParams.id, userCache.user.id, loadUserById]);

  const getAdminTags = async () => {
    const config = {
      params: {
        limit: 50,
        page: 0,
      },
    };
    const adminTagResponse = (await AdminTagService.getByFilter(config)).data;

    if (adminTagResponse.data) {
      setAdminTags(adminTagResponse.data);
    } else {
      setAdminTags([]);
    }
  };

  const getUserLocalGroups = useCallback(async () => {
    if (searchParams.id) {
      const config = {
        params: {
          parentId: searchParams.id,
        },
      };
      const localGroupResponse = (await ParentLocalGroupsService.getByFilter(config)).data;

      if (localGroupResponse.data) {
        setLocalGroups(localGroupResponse.data);
      } else {
        setLocalGroups([]);
      }
    }
  }, [searchParams.id]);

  const getUserAdminTags = useCallback(async () => {
    if (searchParams.id) {
      const tagResponse = await UserAdminTagService.getByFilter(
        `?userId=${searchParams.id}`,
      );

      setUserAdminTags(tagResponse.data.data);
    }
  }, [searchParams.id]);

  const getUserCompany = useCallback(async () => {
    setUserCompany(null);

    if (!searchParams.id) {
      return;
    }

    const userId = Number(searchParams.id);
    const companyEmployees = (await CompanyEmployeeService.findByFilter({ params: { userId } })).data?.data;
    const companyId = companyEmployees?.find(employee => employee.disabled === null)?.companyId;

    if (!companyId) {
      return;
    }

    const company = (await CompanyService.get(companyId)).data?.data;

    if (company) {
      setUserCompany(company);
    }
  }, [searchParams.id]);

  const getUserParent = useCallback(async () => {
    setUserParent(null);

    if (!searchParams.id) {
      return;
    }

    if (!userState.user.id) {
      userState.user.id = Number(searchParams.id);
    }

    const resParent = await ParentService.get(userState.user.id);
    const parent =
      resParent && resParent.data && resParent.data.data
        ? resParent.data.data
        : null;

    setUserParent(parent);
  }, [searchParams.id]);

  useEffect(() => {
    setUserState(userCache);
    if (userCache.user.id && userCache.profile.id) {
      getAdminTags();
      getUserAdminTags();
      getUserCompany();

      if (userCache.user.parent) {
        getUserLocalGroups();
        getUserParent();
      }
    }
  }, [
    getUserAdminTags,
    getUserCompany,
    getUserLocalGroups,
    getUserParent,
    userCache,
    userCache.user.id,
    userCache.profile.id,
    searchParams.id,
  ]);

  const isAgency = (user: User) => !user.parent && !user.sitter;

  const hasSocialConnections = () => !!userState.user.facebookId || !!userState.user.googleUserId;

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setShowSearchResults(!!term);
    if (term) {
      userActions.setUser({});
      userActions.setProfile({});
      setUserState({ user: {}, profile: {} });
      history.push({ search: `?search=${encodeURIComponent(term)}` });
    } else {
      history.push({ search: '' });
    }
  };

  const handleSelectUser = (result: AdminUserSearchResult) => {
    setSearchTerm('');
    setShowSearchResults(false);
    if (result.id) {
      userActions.setUser({});
      userActions.setProfile({});
      setUserState({ user: {}, profile: {} });
      history.push({ search: `?id=${result.id}` });
    }
  };

  const handleSelectRecentUser = (userId: number) => {
    history.push({ search: `?id=${userId}` });
  };

  const showRecentUsers = !searchParams.id && !showSearchResults && !userState.user.id;

  return (
    <Paper className={classes.paper}>
      <AppBar position='static' color='transparent' elevation={0}>
        <Toolbar>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              <UserSearch
                onSearch={handleSearch}
                onSelectUser={handleSelectUser}
              />
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <div
        style={{
          margin: '40px 16px',
          paddingBottom: '20px',
        }}
      >
        {showSearchResults && (
          <UserSearchResults
            searchTerm={searchTerm}
            onSelectUser={handleSelectUser}
          />
        )}
        {showRecentUsers && (
          <RecentlyAccessedUsers onSelectUser={handleSelectRecentUser} />
        )}
        {userState.user.id && userState.profile.id && (
          <div
            style={{
              borderLeft: !userState.user.active
                ? '2px solid rgba(211, 47, 47, 0.5)'
                : userCompany
                  ? '2px solid rgba(156, 39, 176, 0.3)'
                  : 'none',
              paddingLeft: !userState.user.active || userCompany ? '12px' : '0',
              backgroundColor: !userState.user.active
                ? 'rgba(211, 47, 47, 0.08)'
                : userCompany
                  ? 'rgba(156, 39, 176, 0.035)'
                  : 'transparent',
              borderRadius: !userState.user.active || userCompany ? '4px' : '0',
            }}
          >
            <LookupTabs user={userState.user} />
            <UserCard
              lookupUser={userState}
              userAdminTags={userAdminTags}
              userCompany={userCompany}
            />

            {!isAgency(userState.user) && (
              <>
                {userState.user.parent && <Partners userCompany={userCompany} />}
                <PaymentInfo
                  profile={userState.profile}
                  userId={userState.user.id}
                  isParent={userState.user.parent}
                />
                {userState.user.parent && <IdentityCheck userId={userState.user.id} />}
                {userState.user.sitter && (
                  <SitterProfileStatus
                    lookupUser={userState}
                    userId={userState.user.id}
                  />
                )}
                <UserAdminNotes
                  adminUserId={authenticationState.authentication.user.id}
                  userId={userState.user.id}
                />
                {userState.user.sitter && (
                  <>
                    <BackgroundCheck userId={userState.user.id} />
                    <UserPhotoHistory userId={userState.user.id} />
                    <UserVideoModeration style={{ paddingTop: 10 }} userId={userState.user.id} />
                  </>
                )}
                <UserVouchers userId={userState.user.id} />
                {userParent?.primaryAccountID && (
                  <SharedAccounts parent={userParent} />
                )}
                {hasSocialConnections() && <UserSocialConnections user={userState.user} />}
                <UserDevices userId={userState.user.id} />
                {adminTags.length > 0 && (
                  <UserAdminTags
                    lookupUser={userState}
                    adminTags={adminTags}
                    userAdminTags={userAdminTags}
                    getUserAdminTags={getUserAdminTags}
                  />
                )}
                {userState.user.parent && localGroups.length > 0 && (
                  <ParentLocalGroups
                    localGroups={localGroups}
                    getUserLocalGroups={getUserLocalGroups}
                  />
                )}
                <UserNotifications userId={userState.user.id} />
                <UserGroupEdit userId={userState.user.id} />
                <ScammerOverride userId={userState.user.id} />
              </>
            )}
          </div>
        )}
        {searchParams.id && !userState.user.id && (
          <Typography
            color='textPrimary'
            align='center'
            component='h5'
            variant='h5'
            style={{ padding: 20 }}
          >
            User not found
          </Typography>
        )}
      </div>
    </Paper>
  );
}

export default withRouter(UserLookup);
